import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Users, GameController, Storefront, Trophy, CalendarDots, ChartBar,
  Flag, Lightning, Buildings, BookOpen, ShieldCheck, CreditCard, ArrowRight
} from '@phosphor-icons/react';
import { KPICard } from '../../components/admin/AdminComponents';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API_URL}/api/admin-console/dashboard`, { withCredentials: true })
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="space-y-4">
      <div className="h-7 w-48 bg-white/5 rounded animate-pulse" />
      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-3">
        {[...Array(10)].map((_, i) => <div key={i} className="h-24 bg-white/[0.03] rounded-lg animate-pulse" />)}
      </div>
    </div>
  );

  if (!data) return <p className="text-[#444]">Failed to load dashboard.</p>;

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-5">
        <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Unbounded' }}>Platform Overview</h1>
        <p className="text-[13px] text-[#444] mt-0.5">Real-time KPIs and operational status</p>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-3 mb-6">
        <KPICard icon={Users} label="Users" value={data.total_users} accent="#00F0FF" subtext={`+${data.new_users_week} this week`} onClick={() => navigate('/admin/users')} />
        <KPICard icon={GameController} label="Sessions" value={data.total_sessions} accent="#9D4CDD" subtext={`${data.active_sessions} active`} />
        <KPICard icon={Storefront} label="Shops" value={data.total_shops} accent="#F59E0B" subtext={`${data.verified_shops} verified`} onClick={() => navigate('/admin/shops')} />
        <KPICard icon={Buildings} label="Publishers" value={data.total_publishers} accent="#3B82F6" subtext={`${data.verified_publishers} verified`} onClick={() => navigate('/admin/publishers')} />
        <KPICard icon={Trophy} label="Tournaments" value={data.total_tournaments} accent="#10B981" subtext={`${data.active_tournaments} active`} />
        <KPICard icon={Lightning} label="Games" value={data.total_games} accent="#EC4899" onClick={() => navigate('/admin/games')} />
        <KPICard icon={CalendarDots} label="Events" value={data.total_events} accent="#8B5CF6" />
        <KPICard icon={BookOpen} label="Rulesets" value={data.total_rulesets} accent="#00F0FF" onClick={() => navigate('/admin/rules')} />
        <KPICard icon={CreditCard} label="Premium" value={data.total_premium} accent="#F59E0B" />
        <KPICard icon={Flag} label="Reports" value={data.pending_reports} accent="#EF4444" subtext="Pending review" onClick={() => navigate('/admin/reports')} />
      </div>

      {/* Action Queues */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Verification Queue */}
        <div className="bg-[#0c0c14] border border-white/[0.06] rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ShieldCheck size={15} weight="duotone" className="text-[#F59E0B]" />
              <h3 className="text-[13px] font-semibold text-white">Verification Queue</h3>
            </div>
            <button onClick={() => navigate('/admin/verification')} className="text-[11px] text-[#555] hover:text-[#EF4444] flex items-center gap-1">
              View all <ArrowRight size={10} />
            </button>
          </div>
          <div className="space-y-2">
            {data.unverified_shops > 0 && (
              <div className="flex items-center justify-between py-1.5 px-2.5 rounded-md bg-white/[0.02]">
                <div className="flex items-center gap-2">
                  <Storefront size={13} className="text-[#F59E0B]" />
                  <span className="text-[12px] text-[#999]">Shops pending</span>
                </div>
                <span className="text-[12px] font-bold text-[#F59E0B] tabular-nums">{data.unverified_shops}</span>
              </div>
            )}
            {data.unverified_publishers > 0 && (
              <div className="flex items-center justify-between py-1.5 px-2.5 rounded-md bg-white/[0.02]">
                <div className="flex items-center gap-2">
                  <Buildings size={13} className="text-[#3B82F6]" />
                  <span className="text-[12px] text-[#999]">Publishers pending</span>
                </div>
                <span className="text-[12px] font-bold text-[#3B82F6] tabular-nums">{data.unverified_publishers}</span>
              </div>
            )}
            {data.unverified_shops === 0 && data.unverified_publishers === 0 && (
              <p className="text-[12px] text-[#333] text-center py-2">All clear</p>
            )}
          </div>
        </div>

        {/* Top Games */}
        <div className="bg-[#0c0c14] border border-white/[0.06] rounded-lg p-4">
          <h3 className="text-[13px] font-semibold text-white mb-3 flex items-center gap-2">
            <ChartBar size={15} weight="duotone" className="text-[#9D4CDD]" /> Top Games
          </h3>
          <div className="space-y-1.5">
            {data.top_games?.length > 0 ? data.top_games.map((g, i) => (
              <div key={i} className="flex items-center justify-between py-1">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-[#333] w-4 text-right tabular-nums">{i + 1}</span>
                  <span className="text-[12px] text-[#ccc]">{g.game}</span>
                </div>
                <span className="text-[11px] text-[#00F0FF] font-medium tabular-nums">{g.count}</span>
              </div>
            )) : <p className="text-[12px] text-[#333] text-center py-2">No data</p>}
          </div>
        </div>

        {/* Platform Health */}
        <div className="bg-[#0c0c14] border border-white/[0.06] rounded-lg p-4">
          <h3 className="text-[13px] font-semibold text-white mb-3">Platform Status</h3>
          <div className="space-y-2.5">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />
              <span className="text-[12px] text-[#999]">All services operational</span>
            </div>
            <div className="flex items-center gap-2">
              <div className={`w-1.5 h-1.5 rounded-full ${data.pending_reports > 0 ? 'bg-[#F59E0B]' : 'bg-[#10B981]'}`} />
              <span className="text-[12px] text-[#999]">
                {data.pending_reports > 0 ? `${data.pending_reports} reports pending` : 'No pending reports'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-[#00F0FF]" />
              <span className="text-[12px] text-[#999]">{data.active_sessions} live sessions</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-[#9D4CDD]" />
              <span className="text-[12px] text-[#999]">{data.active_tournaments} active tournaments</span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Users */}
      {data.recent_users?.length > 0 && (
        <div className="bg-[#0c0c14] border border-white/[0.06] rounded-lg p-4">
          <h3 className="text-[13px] font-semibold text-white mb-3">Recent Signups</h3>
          <div className="flex flex-col gap-2">
            {data.recent_users.map(u => (
              <div key={u.id} className="flex items-center gap-3 px-3 py-2 rounded-md bg-white/[0.02] border border-white/[0.04]">
                <div className="w-8 h-8 rounded bg-[#00F0FF]/10 flex items-center justify-center text-[12px] text-[#00F0FF] font-bold flex-shrink-0">
                  {(u.name || '?')[0].toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[12px] text-white font-medium truncate">{u.name}</p>
                  <p className="text-[10px] text-[#666] truncate">{u.email}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
